import { User } from '../../store/types';
import { PaginationTypeResponse, TypeSafeResponseType } from '../types';
import { FormValues } from '../../components/form/types';
import { ApiFactoryClassType } from '../factory';

const userAdmin = (factory: ApiFactoryClassType) => ({
    getUsers: async (
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: 'asc' | 'desc',
    ): Promise<PaginationTypeResponse<User> | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/user/list?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getUser: async (id: string): Promise<TypeSafeResponseType<User> | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/admin/user/${id}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createUser: async (userObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/admin/user/create', factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(userObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                        'Accept-Language': factory.getRequestLocale(),
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updateUser: async (id: string, userObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/admin/user/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        ...userObj,
                        roles: Array.from(userObj.roles),
                    }),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                        'Accept-Language': factory.getRequestLocale(),
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getUserClients: async (
        userId: string,
        limit: number = 15,
        offset: number = 0,
    ): Promise<PaginationTypeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/user/${userId}/clients?limit=${limit}&offset=${offset}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
});

export default userAdmin;
